import React, { useState, useEffect } from "react";
import Icon from "../../components/ui/icon";
import "../../assets/styles/pages/publishers/postback.css";
import SideNav from "../../components/layout/sideNav";
import { useToast } from "../../context/toastContext";
import { useNavigate } from "react-router-dom";
import api from "../../services/apiClient";
import DOMPurify from "dompurify";

const GlobalPostbackPage = () => {
  const navigate = useNavigate();
  const showToast = useToast();
  const [isSideNavActive, setisSideNavActive] = useState(false);
  const [userInfo, setUserInfo] = useState(
    JSON.parse(localStorage.getItem("PublisherInfo")) || {}
  );
  const [isPublisherVerified, setisPublisherVerified] = useState(false);
  const [postbackUrl, setpostbackUrl] = useState("");
  const [saveBtnD, setSaveBtnD] = useState(false);
  const [deleteBtnD, setDeleteBtnD] = useState(false);
  const [metaSaving, setMetaSaving] = useState(false);
  const [metaTesting, setMetaTesting] = useState(false);
  const [metaDeleting, setMetaDeleting] = useState(false);
  const [pixelId, setPixelId] = useState("");
  const [accessToken, setAccessToken] = useState("");
  const [eventMappings, setEventMappings] = useState([]);
  const [internalEvent, setInternalEvent] = useState("");
  const [metaEvent, setMetaEvent] = useState("");
  const [loadingEvents, setLoadingEvents] = useState(true);
  const [addingEvent, setAddingEvent] = useState(false);
  const [deletingEvent, setDeletingEvent] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem("aToken");
    if (!token) {
      navigate("/auth/login");
      return;
    }

    if (userInfo.globalPostback) {
      setpostbackUrl(userInfo.globalPostback);
    }

    if (isPublisherVerified) return;

    api
      .get(`/api/publisher/info`)
      .then((response) => {
        setisPublisherVerified(true);
        setUserInfo(response.data.data);
        if (response.data.data.globalPostback) {
          setpostbackUrl(response.data.data.globalPostback);
        } else {
          setpostbackUrl("");
        }
        localStorage.setItem(
          "PublisherInfo",
          JSON.stringify(response.data.data)
        );

        api.get(`/api/meta/integration`).then((res) => {
          if (res.status) {
            setPixelId(res.data?.data?.pixelId);
          }
        }).catch();

        api
          .get("/api/meta/events")
          .then((res) => {
            if (res.data.status) {
              setEventMappings(res.data.events || []);
            }
            setLoadingEvents(false);
          })
          .catch(() => {
            showToast({
              status: "error",
              message: "Failed to load event mappings.",
            });
            setLoadingEvents(false);
          });
      })
      .catch((err) => {
        showToast({
          status: "error",
          message: err.response?.data?.message || "Login not found.",
        });
        navigate("/auth/login");
      });
  }, [isPublisherVerified, navigate, showToast, userInfo]);

  const sanitizeInput = (value) => DOMPurify.sanitize(value);

  const handelPostbackChange = (e) => {
    const value = sanitizeInput(e.target.value);
    setpostbackUrl(value);
  };

  const handelSaveBtnClick = () => {
    if (saveBtnD) return;
    setSaveBtnD(true);
    const url = sanitizeInput(postbackUrl);
    if (!/^(https?:\/\/)?([\w-]+(\.[\w-]+)+)(\/[\w\-./?%&=:{}]*)?$/.test(url)) {
      showToast({
        status: "error",
        message: "Invalid URL",
      });
      setSaveBtnD(false);
      return;
    }

    api
      .put("/api/publisher/update-global-postback", { url })
      .then((response) => {
        const fixedMessage = response.data.status
          ? "Saved Successfully."
          : "Something went wrong.";
        showToast({
          status: response.data.status ? "success" : "error",
          message: response.data.message || fixedMessage,
        });
        setSaveBtnD(false);
      })
      .catch((err) => {
        showToast({
          status: "error",
          message: err.response?.data?.message || "Something went wrong.",
        });
        setSaveBtnD(false);
      });
  };

  const handelDeleteBtnClick = () => {
    if (deleteBtnD) return;
    setDeleteBtnD(true);
    api
      .delete("/api/publisher/delete-global-postback")
      .then((response) => {
        const fixedMessage = response.data.status
          ? "Deleted Successfully."
          : "Something went wrong.";
        showToast({
          status: response.data.status ? "error" : "error",
          message: response.data.message || fixedMessage,
        });
        setDeleteBtnD(false);

        if (response.data.status) {
          setpostbackUrl("");
        }
      })
      .catch((err) => {
        showToast({
          status: "error",
          message: err.response?.data?.message || "Something went wrong.",
        });
        setDeleteBtnD(false);
      });
  };

  const handleMetaIntegrate = () => {
    if (metaSaving) return;

    if (!pixelId || !accessToken) {
      return showToast({
        status: "error",
        message: "Pixel ID and Access Token are required.",
      });
    }

    setMetaSaving(true);

    api
      .post("/api/meta/integration", {
        pixelId,
        accessToken,
      })
      .then((response) => {
        showToast({
          status: response.data.status ? "success" : "error",
          message:
            response.data.message || "Meta integration saved successfully.",
        });

        if (response.data.status) {
          setAccessToken("");
        }

        setMetaSaving(false);
      })
      .catch((err) => {
        showToast({
          status: "error",
          message: err.response?.data?.message || "Something went wrong.",
        });
        setMetaSaving(false);
      });
  };

  const handleMetaTest = () => {
    if (metaTesting) return;

    setMetaTesting(true);

    api
      .post("/api/meta/test-event")
      .then((response) => {
        showToast({
          status: response.data.status ? "success" : "error",
          message:
            response.data.message || "Test event executed successfully.",
        });

        setMetaTesting(false);
      })
      .catch((err) => {
        showToast({
          status: "error",
          message: err.response?.data?.message || "Test event failed.",
        });
        setMetaTesting(false);
      });
  };

  const handleMetaDelete = () => {
    if (metaDeleting) return;

    setMetaDeleting(true);

    api
      .delete("/api/meta/integration")
      .then((response) => {
        showToast({
          status: response.data.status ? "success" : "error",
          message:
            response.data.message || "Meta integration removed successfully.",
        });

        if (response.data.status) {
          setPixelId("");
          setAccessToken("");
        }

        setMetaDeleting(false);
      })
      .catch((err) => {
        showToast({
          status: "error",
          message: err.response?.data?.message || "Something went wrong.",
        });
        setMetaDeleting(false);
      });
  };

  const handleAddEventMapping = () => {
    if (addingEvent) return;

    if (!internalEvent || !metaEvent) {
      return showToast({
        status: "error",
        message: "Both Internal Event and Meta Event are required.",
      });
    }

    setAddingEvent(true);

    api
      .post("/api/meta/events", {
        internalEvent,
        metaEvent,
      })
      .then((res) => {
        showToast({
          status: res.data.status ? "success" : "error",
          message: res.data.message || "Event mapping added.",
        });

        if (res.data.status) {
          setInternalEvent("");
          setMetaEvent("");
          setEventMappings((prev) => [...prev, res.data?.event])
        }

        setAddingEvent(false);
      })
      .catch((err) => {
        showToast({
          status: "error",
          message: err.response?.data?.message || "Something went wrong.",
        });
        setAddingEvent(false);
      });
  };

  const handleDeleteEventMapping = (id) => {
    if (deletingEvent) return;
    setDeletingEvent(id);
    api
      .delete(`/api/meta/events/${id}`)
      .then((res) => {
        showToast({
          status: res.data.status ? "success" : "error",
          message: res.data.message || "Event mapping deleted.",
        });

        if (res.data.status) {
          setEventMappings((prev) =>
            prev.filter((event) => event._id !== id)
          );
        }

        setDeletingEvent(null);
      })
      .catch(() => {
        showToast({
          status: "error",
          message: "Failed to delete event mapping.",
        });
        setDeletingEvent(null);
      });
  };

  return (
    <div className="postback__page">
      <SideNav
        userName={userInfo.name}
        emailID={userInfo.email}
        active={isSideNavActive}
        campaignOpenInital={false}
        accManagerName={userInfo.adminInfo?.name}
        accManagerEmail={userInfo.adminInfo?.email}
      />

      <div
        onClick={() => setisSideNavActive(!isSideNavActive)}
        className={
          isSideNavActive ? "background-shadow active" : "background-shadow"
        }
      ></div>

      <main>
        <header className="desktop-header">
          <div>
            <h2>Global Postback</h2>
            <p>Configure where conversion events are sent in real time.</p>
          </div>

          <button onClick={() => navigate("/postback-logs")}>
            <Icon name="ListBullet" className="onLeft" />
            Postback Logs
          </button>
        </header>

        <header className="mobile-header">
          <h2>Global Postback</h2>
          <button
            className="nav-btn"
            onClick={() => setisSideNavActive(!isSideNavActive)}
          >
            {isSideNavActive ? (
              <Icon name="XMark" />
            ) : (
              <Icon name="Bars3" />
            )}
          </button>
        </header>

        <div className="main-container">
          <section className="card postback-editor">
            <div className="card-header">
              <h3>Postback URL</h3>
              <span className="badge">Global</span>
            </div>

            <textarea
              placeholder="https://example.com/postback?click_id={aff_click_id}&goal={goal}"
              value={postbackUrl}
              onChange={handelPostbackChange}
              rows={4}
            />

            <div className="editor-actions">
              <button
                className="primary"
                disabled={saveBtnD}
                onClick={handelSaveBtnClick}
              >
                {saveBtnD ? "Saving" : "Save"}
                <Icon name={saveBtnD ? "ArrowPath" : "Check"} className={saveBtnD ? "spin" : ""} />
              </button>

              <button
                className="danger"
                onClick={handelDeleteBtnClick}
                disabled={deleteBtnD}
              >
                {deleteBtnD ? "Removing" : "Remove"}
                <Icon name={deleteBtnD ? "ArrowPath" : "Trash"} className={deleteBtnD ? "spin" : ""} />
              </button>
            </div>
          </section>

          <section className="card server-ip-card">
            <h3>Postback Server IP</h3>
            <code className="ip-address">82.25.105.112</code>
            <p>
              Whitelist this IP if your system restricts inbound requests.
              All postbacks originate from this address.
            </p>
          </section>

          <section className="card placeholders-card">
            <h3>Available Placeholders</h3>

            <div className="placeholders-grid">
              {[
                ["{aff_click_id}", "Primary click identifier"],
                ["{sub_aff_id}", "Secondary tracking value"],
                ["{goal}", "Conversion goal name"],
                ["{payout}", "Conversion payout"],
                ["{currency}", "INR / USD / EUR"],
                ["{ip}", "Click IP address"],
                ["{user_agent}", "Browser user agent"],
                ["{source}", "Traffic source"],
                ["{click_time}", "Click timestamp"],
                ["{conversion_time}", "Conversion timestamp"],
                ["{gaid}", "Google Advertising ID"],
              ].map(([key, desc]) => (
                <div className="placeholder-pill" key={key}>
                  <code>{key}</code>
                  <span>{desc}</span>
                </div>
              ))}
            </div>
          </section>

          <section className="card meta-integration-card">
            <div className="card-header">
              <div>
                <h3>Meta Ads Integration</h3>
                <p>Send conversion events directly to Meta Ads using your Pixel.</p>
              </div>
              <span className="badge">Optional</span>
            </div>

            <div className="meta-form">
              <div className="field">
                <label>Pixel ID</label>
                <input
                  type="text"
                  placeholder="123456789012345"
                  value={pixelId ? pixelId : ""}
                  onChange={(e) => setPixelId(e.target.value)}
                />
              </div>

              <div className="field">
                <label>Access Token</label>
                <input
                  type="text"
                  placeholder="EAABsbCS1iHgBA..."
                  value={accessToken ? accessToken : ""}
                  onChange={(e) => setAccessToken(e.target.value)}
                />
                <span className="field-hint">
                  Token is encrypted and never shown again after saving.
                </span>
              </div>
            </div>

            <div className="editor-actions">
              <button
                className="secondary"
                onClick={handleMetaTest}
                disabled={metaTesting}
              >
                <Icon name={metaTesting ? "ArrowPath" : "Beaker"} className={metaTesting ? "spin" : ""} />
                {metaTesting ? "Testing" : "Test"}
              </button>

              <button
                className="primary"
                onClick={handleMetaIntegrate}
                disabled={metaSaving}
              >
                <Icon name={metaSaving ? "ArrowPath" : "Link"} className={metaSaving ? "spin" : ""} />
                {metaSaving ? "Integrating" : "Integrate"}
              </button>

              <button
                className="danger"
                onClick={handleMetaDelete}
                disabled={metaDeleting}
              >
                <Icon name={metaDeleting ? "ArrowPath" : "Trash"} className={metaDeleting ? "spin" : ""} />
                {metaDeleting ? "Removing" : "Remove"}
              </button>
            </div>

            <p className="meta-footnote">
              Events are sent server-to-server. Browser ad-blockers do not affect tracking.
            </p>
          </section>

          <section className="card event-mapping-card">
            <div className="card-header">
              <div>
                <h3>Event Mapping</h3>
                <p>Map your internal events to Meta standard events.</p>
              </div>
              <span className="badge">
                {eventMappings.length}/15
              </span>
            </div>

            <div className="event-mapping-form">
              <input
                type="text"
                placeholder="Goal in our panel (e.g. registration)"
                value={internalEvent}
                onChange={(e) => setInternalEvent(e.target.value)}
              />

              <input
                type="text"
                placeholder="Meta Event (e.g. CompleteRegistration)"
                value={metaEvent}
                onChange={(e) => setMetaEvent(e.target.value)}
              />

              <button
                className="primary"
                disabled={addingEvent || eventMappings.length >= 15}
                onClick={handleAddEventMapping}
              >
                <Icon
                  name={addingEvent ? "ArrowPath" : "Plus"}
                  className={addingEvent ? "spin" : ""}
                />
                Add
              </button>
            </div>

            <div className="event-mapping-list">
              {loadingEvents ? (
                <p className="empty-text">Loading events...</p>
              ) : eventMappings.length === 0 ? (
                <p className="empty-text">No event mappings added.</p>
              ) : (
                eventMappings.map((event) => (
                  <div className="event-row" key={event._id}>
                    <div>
                      <span className="internal">{event.internalEvent}</span>
                      <Icon name="ArrowRight" />
                      <span className="meta">{event.metaEvent}</span>
                    </div>

                    <button
                      className="danger-icon"
                      onClick={() => handleDeleteEventMapping(event._id)}
                    >
                      {deletingEvent === event._id ? <Icon name="ArrowPath" className={"spin"} /> : <Icon name="Trash" />}
                    </button>
                  </div>
                ))
              )}
            </div>
          </section>
        </div>

        <span className="copyright-text">
          © 2025 Swift Digital Ads. All rights reserved.
        </span>
      </main>
    </div>

  );
};

export default GlobalPostbackPage;
